import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress
} from '@mui/material';
import { Save, Info } from 'lucide-react';
import { FilterFieldSx } from '../../../../constants/style';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { changePassword } from '../../../../apis/Masters/Users';
import { localStorageKeyName } from '../../../../constants/constant';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};
  if (!form.oldPassword.trim()) {
    errors.oldPassword = 'Current Password is required.';
  }
  if (!form.newPassword.trim()) {
    errors.newPassword = 'New Password is required.';
  }
  if (!form.confirmPassword) errors.confirmPassword = 'Please confirm your password.';
  else if (form.confirmPassword !== form.newPassword)
    errors.confirmPassword = 'Passwords do not match.';
  return errors;
};

const ChangePassword = ({ userAccess, onRefresh, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const user = localStorage.getItem(localStorageKeyName);
  const userDetails = user ? JSON.parse(user) : null;

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const passwordHandler = () => {
    openConfirm({
      title: 'Change Password',
      content: `Are you sure you want to change the password?`,
      onConfirm: () => {
        closeConfirm();
        handleSave();
      }
    });
  };

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : (e.target.value || '').toUpperCase();
    const next = { ...form, [field]: value };
    setForm(next);

    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const payload = {
      ...form,
      userName: userDetails?.userName
    }
    setSaving(true);
    try {
      const response = await changePassword(payload);
      if (response?.success) {
        onClose?.();
        onRefresh?.();
      }
      console.log('Change Passowrd ', response);
    } catch (err) {
      console.log('Failed to change password:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
          {/* Name */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Current Password"
              placeholder="e.g. Password"
              value={form.oldPassword}
              onChange={handleChange('oldPassword')}
              onBlur={handleBlur('oldPassword')}
              error={touched.oldPassword && Boolean(errors.oldPassword)}
              helperText={touched.oldPassword && errors.oldPassword}
              size="small"
              sx={FilterFieldSx}
            />
          </Box>

          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="New Password"
              placeholder="Enter New Password"
              value={form.newPassword}
              onChange={handleChange('newPassword')}
              onBlur={handleBlur('newPassword')}
              error={touched.newPassword && Boolean(errors.newPassword)}
              helperText={touched.newPassword && errors.newPassword}
              size="small"
              sx={FilterFieldSx}
            />
          </Box>

          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Confirm Password"
              placeholder="Repeat password"
              value={form.confirmPassword}
              onChange={handleChange('confirmPassword')}
              onBlur={handleBlur('confirmPassword')}
              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              size="small"
              sx={FilterFieldSx}
            />
          </Box>
        </Box>

        {/* ── Actions ─────────────────────────────────────────────────────── */}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
            onClick={passwordHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)'
            }}
          >
            {saving ? 'Changing…' : 'Change'}
          </Button>
        </Box>
      </Box>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Info size={18} />}
        confirmText="Are you sure"
        cancelText="Cancel"
        confirmColor="info"
      />
    </>
  );
};

export default ChangePassword;
